/**
 * Turn clock — the minimal Phase 1 timeline panel (phase1-plan §5: ordered
 * token list, round counter, current-turn highlight, Next Turn button) and,
 * in the same element, the §6.2 `[data-testid="turn-clock"]` mirror
 * (`data-round`, `data-current-token`, `data-begun`). One element serves
 * both jobs so the mirror can never disagree with what the table sees.
 *
 * Honest states (PO rule: a disabled control that doesn't say why is a
 * silent no-op wearing a different hat):
 *  - not begun, no entries → explains how to build the order; no mute button
 *  - not begun, entries    → GM sees "Begin combat"; begun makes the button
 *    IMPOSSIBLE rather than disabled — it transforms into "Next turn" + the
 *    live round display (the honest next action)
 *  - `begun` comes off the wire and survives server restarts, so the
 *    control can trust it across reconnects.
 *
 * GM initiative-building lives here too: an add-token select over tokens
 * not yet in the order (duplicates are validator-rejected — decision 5 —
 * so the UI simply doesn't offer them). Removal/reorder UI is deliberately
 * absent: reorder is Phase 2, and entry removal waits on QA's last-actor
 * confirm fixture.
 *
 * Pure render + callbacks, idempotent, no net/ coupling.
 */

import type { Timeline, Token } from "@asarimitsuha/table-core";

export interface TurnClockOptions {
  /** Shows the GM controls (begin/advance/add). Server still validates. */
  isGm: boolean;
}

export interface TurnClockCallbacks {
  onBegin?(): void;
  onAdvance?(): void;
  onAddToken?(tokenId: string): void;
}

/** Display name for a timeline entry: the token's name, the entry's label
 *  (Phase 2 non-token slots), or the raw token id as a last resort — never
 *  an empty string, because a blank initiative row reads as a bug. */
function entryName(
  entry: Timeline["entries"][number],
  tokens: Record<string, Token>,
): string {
  if (entry.tokenId !== null) {
    return tokens[entry.tokenId]?.name ?? entry.tokenId;
  }
  return entry.label ?? entry.id;
}

function renderAddControl(
  timeline: Timeline,
  tokens: Record<string, Token>,
  cb: TurnClockCallbacks,
): HTMLElement | null {
  const onTimeline = new Set(
    timeline.entries.map((e) => e.tokenId).filter((id) => id !== null),
  );
  const addable = Object.values(tokens)
    .filter((t) => !onTimeline.has(t.id))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  if (addable.length === 0) return null;

  const form = document.createElement("form");
  form.className = "turn-clock-add";

  const label = document.createElement("label");
  label.textContent = "Add to initiative";
  const select = document.createElement("select");
  select.dataset["testid"] = "initiative-add-select";
  for (const token of addable) {
    const option = document.createElement("option");
    option.value = token.id;
    option.textContent = token.name;
    select.appendChild(option);
  }
  label.appendChild(select);

  const button = document.createElement("button");
  button.type = "submit";
  button.dataset["testid"] = "initiative-add";
  button.textContent = "Add";

  form.append(label, button);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (select.value !== "") cb.onAddToken?.(select.value);
  });
  return form;
}

const NO_OPTS: TurnClockOptions = { isGm: false };

/**
 * Render the turn clock for `timeline` into `root`. `tokens` resolves entry
 * names and feeds the add control. Idempotent (replaces children).
 */
export function renderTurnClock(
  root: HTMLElement,
  timeline: Timeline,
  tokens: Record<string, Token>,
  options: TurnClockOptions = NO_OPTS,
  cb: TurnClockCallbacks = {},
): void {
  root.replaceChildren();
  const begun = timeline.begun === true;
  root.dataset["testid"] = "turn-clock";
  root.dataset["round"] = String(timeline.round);
  root.dataset["begun"] = String(begun);

  const current = timeline.entries[timeline.currentIndex];
  root.dataset["currentToken"] = current?.tokenId ?? "";

  const heading = document.createElement("h3");
  heading.textContent = begun ? `Round ${timeline.round}` : "Combat not started";
  root.appendChild(heading);

  if (timeline.entries.length === 0) {
    const empty = document.createElement("p");
    empty.className = "turn-clock-empty";
    empty.textContent = options.isGm
      ? "No initiative order yet — add tokens below, then begin combat."
      : "No initiative order yet.";
    root.appendChild(empty);
  } else {
    const list = document.createElement("ol");
    list.className = "turn-clock-entries";
    list.setAttribute("aria-label", "Initiative order");

    timeline.entries.forEach((entry, index) => {
      const item = document.createElement("li");
      item.dataset["entryId"] = entry.id;
      item.dataset["tokenId"] = entry.tokenId ?? "";
      // Before combat begins nobody is acting; highlighting slot 0 would lie.
      const isCurrent = begun && index === timeline.currentIndex;
      item.dataset["current"] = String(isCurrent);
      if (isCurrent) {
        // aria-current announces "current step" to screen readers; the
        // visual highlight hangs off data-current in CSS.
        item.setAttribute("aria-current", "true");
      }
      item.textContent = entryName(entry, tokens);
      list.appendChild(item);
    });
    root.appendChild(list);

    if (options.isGm) {
      // The honest next action, never a greyed twin: Begin before combat,
      // Next turn during it.
      const action = document.createElement("button");
      action.type = "button";
      if (begun) {
        action.dataset["testid"] = "next-turn";
        action.textContent = "Next turn";
        action.addEventListener("click", () => cb.onAdvance?.());
      } else {
        action.dataset["testid"] = "begin-combat";
        action.textContent = "Begin combat";
        action.addEventListener("click", () => cb.onBegin?.());
      }
      root.appendChild(action);
    }
  }

  if (options.isGm) {
    const add = renderAddControl(timeline, tokens, cb);
    if (add) root.appendChild(add);
  }
}
